// Lõi THUẦN suy luận lệnh spawn để bắn OS notification — KHÔNG I/O (không đọc
// file, không spawn). `fileExists` được inject để test không đụng filesystem.
//
// VÌ SAO KHÔNG fallback osascript ngoài AvatarCodi (D5, plan os-notify-agent-
// proactive): Luke đo 25/09 (MR !33 team-ai-pack) — osascript/terminal-notifier
// ad-hoc/osacompile đều bị `usernoted` gán nhầm cho Script Editor hoặc từ chối
// thẳng vì không ký Developer ID. Chỉ app ký Developer ID + notarize qua được
// (report phase-00, xác nhận lại 2026-09-26). Ngoài AvatarCodi ⇒ no-op tuyệt
// đối, không có đường lùi.

import type { HostPlatform } from "./detect-host-platform.js";

export interface NotifyOptions {
  title: string;
  body: string;
  subtitle?: string;
  sound?: boolean;
}

export interface NotifyInvocation {
  command: string;
  args: string[];
}

export interface ResolveNotifyInvocationInput {
  platform: HostPlatform;
  env: NodeJS.ProcessEnv;
  opts: NotifyOptions;
  fileExists: (path: string) => boolean;
}

const MAX_BODY_LENGTH = 200;
const FALLBACK_APP_PATH = "/Applications/AvatarCodi.app";
// Đường trong bundle, đo được ở phase-00 (report §5).
const HELPER_RELATIVE_PATH =
  "Contents/Resources/AvatarCodi Notify.app/Contents/MacOS/avatar-codi-notify";
const AVATARCODI_BUNDLE_ID = "vn.nal.avatar-codi";

/** Cắt về 1 dòng, tối đa 200 ký tự — helper là banner ngắn, không phải log dài. */
function sanitizeBody(raw: string): string {
  const oneLine = raw.replace(/\r?\n/g, " ");
  return oneLine.length > MAX_BODY_LENGTH ? oneLine.slice(0, MAX_BODY_LENGTH) : oneLine;
}

/**
 * Đọc field `defaultMessagesFile` thô từ `VSCODE_NLS_CONFIG` (JSON). Env ngoại
 * lai (extension host khác ghi đè) nên parse lỗi/thiếu field ⇒ null, KHÔNG throw.
 * Dùng chung cho cả darwin (cần cắt tới ".app") và win32 (chỉ cần substring).
 */
function readNlsDefaultMessagesFile(env: NodeJS.ProcessEnv): string | null {
  const raw = env.VSCODE_NLS_CONFIG;
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  const file =
    parsed !== null && typeof parsed === "object" && "defaultMessagesFile" in parsed
      ? (parsed as { defaultMessagesFile?: unknown }).defaultMessagesFile
      : undefined;
  return typeof file === "string" ? file : null;
}

/**
 * Suy `<App>.app` từ `VSCODE_NLS_CONFIG` (kế thừa xuống hook — đo 25/09, D4).
 * Shape thật: JSON có `defaultMessagesFile` = "<App>.app/Contents/Resources/
 * app/out/nls.messages.json". Chỉ áp dụng cho macOS (bundle ".app").
 */
export function resolveAvatarCodiAppPathFromEnv(env: NodeJS.ProcessEnv): string | null {
  const file = readNlsDefaultMessagesFile(env);
  if (!file) return null;

  const marker = ".app/";
  const idx = file.indexOf(marker);
  if (idx === -1) return null;
  return file.slice(0, idx + marker.length - 1);
}

/** Gate D4: chỉ hành động khi host là AvatarCodi (macOS: bundle id hoặc NLS). */
function isAvatarCodiHostDarwin(env: NodeJS.ProcessEnv): boolean {
  if (env.AVATAR_NOTIFY_HELPER) return true;
  if (env.__CFBundleIdentifier === AVATARCODI_BUNDLE_ID) return true;
  const appPath = resolveAvatarCodiAppPathFromEnv(env);
  return appPath?.endsWith("AvatarCodi.app") ?? false;
}

function buildDarwinArgs(opts: NotifyOptions): string[] {
  const args = ["--title", opts.title, "--body", sanitizeBody(opts.body)];
  if (opts.subtitle) args.push("--subtitle", opts.subtitle);
  if (opts.sound) args.push("--sound");
  return args;
}

function resolveDarwinInvocation(
  env: NodeJS.ProcessEnv,
  opts: NotifyOptions,
  fileExists: (path: string) => boolean,
): NotifyInvocation | null {
  if (!isAvatarCodiHostDarwin(env)) return null;

  const helperPath =
    env.AVATAR_NOTIFY_HELPER ??
    `${resolveAvatarCodiAppPathFromEnv(env) ?? FALLBACK_APP_PATH}/${HELPER_RELATIVE_PATH}`;

  if (!fileExists(helperPath)) return null;
  return { command: helperPath, args: buildDarwinArgs(opts) };
}

/** Escape kiểu PowerShell single-quoted string: nhân đôi dấu `'`. */
function escapePowerShellSingleQuoted(s: string): string {
  return s.replace(/'/g, "''");
}

function resolveWin32Invocation(
  env: NodeJS.ProcessEnv,
  opts: NotifyOptions,
): NotifyInvocation | null {
  // Windows không có __CFBundleIdentifier và không có bundle ".app" — chỉ còn
  // tín hiệu substring trên đường dẫn NLS. Best-effort, không nghiệm thu (xem
  // plan.md "Ngoài phạm vi v1").
  const file = readNlsDefaultMessagesFile(env);
  if (!file?.toLowerCase().includes("avatarcodi")) return null;

  const aumid = "NAL.AvatarCodi";
  const title = escapePowerShellSingleQuoted(opts.title);
  const body = escapePowerShellSingleQuoted(sanitizeBody(opts.body));
  // Toast WinRT qua PowerShell — argv MẢNG (không dựng chuỗi shell nối trực
  // tiếp từ input), title/body đã escape trước khi nhúng vào script.
  const script = [
    "$ErrorActionPreference='Stop';",
    "[Windows.UI.Notifications.ToastNotificationManager, Windows.UI.Notifications, ContentType=WindowsRuntime] | Out-Null;",
    "[Windows.Data.Xml.Dom.XmlDocument, Windows.Data.Xml.Dom, ContentType=WindowsRuntime] | Out-Null;",
    "$xml = [Windows.UI.Notifications.ToastNotificationManager]::GetTemplateContent(0);",
    "$t = $xml.GetElementsByTagName('text');",
    `$t.Item(0).AppendChild($xml.CreateTextNode('${title}')) | Out-Null;`,
    `$t.Item(1).AppendChild($xml.CreateTextNode('${body}')) | Out-Null;`,
    "$toast = [Windows.UI.Notifications.ToastNotification]::new($xml);",
    `[Windows.UI.Notifications.ToastNotificationManager]::CreateToastNotifier('${aumid}').Show($toast);`,
  ].join(" ");

  return { command: "powershell", args: ["-NoProfile", "-NonInteractive", "-Command", script] };
}

/**
 * Suy lệnh spawn cho notification OS. `null` = no-op im lặng (ngoài AvatarCodi,
 * helper thiếu, hoặc Linux/unsupported — D-linux: YAGNI, chưa có nhu cầu).
 */
export function resolveNotifyInvocation(
  input: ResolveNotifyInvocationInput,
): NotifyInvocation | null {
  const { platform, env, opts, fileExists } = input;
  if (platform === "darwin") return resolveDarwinInvocation(env, opts, fileExists);
  if (platform === "win32") return resolveWin32Invocation(env, opts);
  return null;
}
